import { config } from "@/config"
import { TCSAPIResponse } from "@/types/types"
import axios, { AxiosError, AxiosResponse } from "axios"
import { formatResponse } from "./helpers/formatResponse"
import {
  CredentialCreateRequestBody,
  Credential as AllCredentialsResponse,
  CredentialDetailsResponseData,
  GoogleSheetTokensResponse,
  UseCaseCredential
} from "@/types/credentials"

export const CredentialsAPI = {
  /**
   * Fetch the credentials list.
   *
   * @param withAvailabilityProbe - When `true` the BE runs a live `getOrProbeSync`
   *   probe per credential domain (2 s cap) and returns `availabilitySummary` for
   *   each dashboard-eligible credential.  Only the dashboard-creation picker
   *   prefetch should pass `true` — it needs freshness-at-commitment.
   *
   *   Admin Credentials page calls MUST omit this (or pass `false`) so page load
   *   fires zero probes and returns only the sticky `connectionStatus` (Slice 1.2
   *   probe-isolation invariant, TDD §"Consumer enumeration").
   */
  getAll: async (token: string, limit = 10, offset = 0, withAvailabilityProbe?: boolean) => {
    try {
      const resp: AxiosResponse<{
        success: boolean
        data: AllCredentialsResponse[]
        total: number
      }> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/credentials`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          limit,
          offset,
          ...(withAvailabilityProbe === true ? { withAvailabilityProbe: true } : {}),
        }
      })

      if (resp.data.success) {
        return {
          data: {
            credentials: resp.data.data,
            total: resp.data.total
          },
          err: undefined
        }
      } else {
        return {
          data: undefined,
          err: 'Failed to fetch credentials'
        }
      }
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return {
        data: undefined,
        err: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  },

  create: async (token: string, data: CredentialCreateRequestBody) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'POST',
        url: `${config.apiUrl}/v1/credentials`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  delete: async (token: string, credentialId: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'DELETE',
        url: `${config.apiUrl}/v1/credentials/${credentialId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  update: async (token: string, credentialId: string, body: CredentialCreateRequestBody) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'PUT',
        url: `${config.apiUrl}/v1/credentials/${credentialId}`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: body
      })

      return formatResponse<TCSAPIResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<TCSAPIResponse>(null, error as AxiosError)
    }
  },

  getCredentialDetails: async (token: string, credentialId: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/credentials/${credentialId}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<CredentialDetailsResponseData>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<CredentialDetailsResponseData>(null, error as AxiosError)
    }
  },

  getGoogleTokensFromAuthCode: async (token: string, authCode: string) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/applications/google_sheets/get_tokens_from_code?code=${authCode}`,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      return formatResponse<GoogleSheetTokensResponse>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<GoogleSheetTokensResponse>(null, error as AxiosError)
    }
  },

  getByUseCase: async (
    token: string,
    appId: string,
    action: string,
    subject: string
  ) => {
    try {
      const resp: AxiosResponse<TCSAPIResponse> = await axios({
        method: 'GET',
        url: `${config.apiUrl}/v1/credentials/use-case`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          appId,
          action,
          subject,
        },
      })

      return formatResponse<UseCaseCredential[]>(resp.data, null)
    } catch (error) {
      console.log('Error: ', error instanceof Error ? error.message : error)
      return formatResponse<UseCaseCredential[]>(null, error as AxiosError)
    }
  },
}
